/**
 * Tanıtım takibi (v0.1.169; Sercan, 11 Oct 2026: "hangi firmaya hangi maili attık, ne zaman attık; gönderilenler ve
 * kalanlar için bir sayfa; taranıp adresi doğrulanan firma sıraya girsin; iyi ölçüm metrikleri olsun"). On top the
 * measures (today against the daily limit, the week, replies, interest, bounces) and the 30 days as bars (the Site
 * chart's way: inline SVG, a title and a hidden table read the numbers); then the first mails that wait with the stage
 * each one is in, the mails that went out with their answers, and the sends by region and by first line.
 * Read only: approvals stay on Tanıtım gönderimi.
 */

import { useEffect, useId, useState } from 'react';
import { MAIL_STATE_LABELS, OUTREACH_REPLY_CLASS_TR } from '../api/outreach.js';
import {
  CAMPAIGN_LABELS,
  OUTREACH_QUEUE_STAGES,
  type OutreachLog,
  type OutreachLogGroup,
  type OutreachLogMail,
  QUEUE_STAGE_LABELS,
} from '../api/outreach-log.js';
import { OUTREACH_ARM_TR } from '../api/prospects.js';
import { usePanel } from '../app/context.js';
import { useIsDesktop } from '../app/media.js';
import { useOutreachLog } from '../app/outreach-log.js';
import { Link } from '../app/router.js';
import { formatClock, formatDayMonth, formatIsoDay, formatNumber, formatRelativeClock } from '../lib/format.js';
import { Icon } from './icons.js';

const RESULT_TARGETS = 'Hedef: cevap oranı %5 ve üstü, geri dönen %2 altı, şikâyet sıfır.';
const INTERESTED = new Set(['positive', 'question', 'referral', 'consent_yes']);
const NOT_INTERESTED = new Set(['negative', 'unsubscribe', 'spam_complaint', 'consent_no', 'bounce_hard']);

function n(value: number): string {
  return formatNumber(value);
}

function rate(value: number | null): string {
  return value === null ? '-' : `%${formatNumber(value, 1)}`;
}

function when(at: number): string {
  return `${formatDayMonth(at)} ${formatClock(at)}`;
}

/** Mails that are past every check and only wait for their turn or Sercan's approval. */
function goingSoon(view: OutreachLog): number {
  const queue = view.totals.queue;
  return queue.scheduled + queue.approved + queue.ready;
}

/** The 30 days as bars: mails out in the quiet colour, answers from people in the accent beside them. */
function OutreachBars({ daily }: { daily: OutreachLog['daily'] }) {
  const titleId = useId();
  const sent = daily.reduce((sum, day) => sum + day.sent, 0);
  const replies = daily.reduce((sum, day) => sum + day.replies, 0);
  if (sent === 0 && replies === 0) return <p className="au-bars__empty">Son 30 günde giden mail yok.</p>;
  const width = 300;
  const height = 56;
  const gap = 2;
  const max = Math.max(1, ...daily.map((day) => Math.max(day.sent, day.replies)));
  const slot = (width - gap * (daily.length - 1)) / Math.max(1, daily.length);
  const first = daily[0]?.day ?? '';
  const last = daily.at(-1)?.day ?? '';
  return (
    <figure className="au-bars ol-bars">
      <svg
        className="au-bars__svg ol-bars__svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-labelledby={titleId}
      >
        <title id={titleId}>{`Son 30 gün: ${n(sent)} mail gitti, ${n(replies)} kişi cevap verdi`}</title>
        <line className="au-bars__base" x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} />
        {daily.map((day, index) => {
          const x = index * (slot + gap);
          const out = (day.sent / max) * (height - 2);
          const answers = (day.replies / max) * (height - 2);
          return (
            <g key={day.day}>
              {out > 0 ? <rect className="ol-bars__sent" x={x} y={height - 1 - out} width={slot} height={out} /> : null}
              {answers > 0 ? (
                <rect
                  className="ol-bars__replies"
                  x={x + slot / 4}
                  y={height - 1 - answers}
                  width={slot / 2}
                  height={answers}
                />
              ) : null}
            </g>
          );
        })}
      </svg>
      <figcaption className="au-bars__cap" aria-hidden="true">
        <span>{formatIsoDay(first)}</span>
        <span>
          <span className="ol-key ol-key--sent" /> giden · <span className="ol-key ol-key--replies" /> cevap
        </span>
        <span>{formatIsoDay(last)}</span>
      </figcaption>
      {/* A table ignores the 1 px width of .sr-only; the wrapper clips it so the page cannot scroll sideways. */}
      <div className="sr-only">
        <table>
          <caption>Tanıtım mailleri: son 30 gün, gün gün</caption>
          <thead>
            <tr>
              <th scope="col">Gün</th>
              <th scope="col">Giden</th>
              <th scope="col">Cevap</th>
            </tr>
          </thead>
          <tbody>
            {daily.map((day) => (
              <tr key={day.day}>
                <th scope="row">{formatIsoDay(day.day)}</th>
                <td>{day.sent}</td>
                <td>{day.replies}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

function Summary({
  view,
  loading,
  error,
  onReload,
}: {
  view: OutreachLog;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}) {
  const clock = usePanel().now;
  const totals = view.totals;
  const soon = goingSoon(view);
  const days = view.dailyLimit > 0 && soon > 0 ? Math.ceil(soon / view.dailyLimit) : null;
  return (
    <section className="au-sum" aria-labelledby="ol-sum-h">
      <h2 id="ol-sum-h" className="au-sum__title">
        Giden: <span className="mono accent">{n(totals.sent)}</span> mail
      </h2>
      <dl className="au-sum__grid">
        <div>
          <dt>Son 24 saat</dt>
          <dd className="mono">
            {n(totals.sentToday)} / {n(view.dailyLimit)}
          </dd>
        </div>
        <div>
          <dt>Son 7 gün</dt>
          <dd className="mono">{n(totals.sent7d)}</dd>
        </div>
        <div>
          <dt>İlk mail · takip</dt>
          <dd className="mono">
            {n(totals.firstMails)} · {n(totals.followUps)}
          </dd>
        </div>
        <div>
          <dt>Cevap veren firma</dt>
          <dd className="mono">
            {n(totals.replied)} · {rate(totals.replyRate)}
          </dd>
        </div>
        <div>
          <dt>İlgilenen</dt>
          <dd className="mono">{n(totals.interested)}</dd>
        </div>
        <div>
          <dt>İlgilenmeyen · listeden çıkan</dt>
          <dd className="mono">
            {n(totals.negative)} · {n(totals.unsubscribed)}
          </dd>
        </div>
        <div>
          <dt>Geri dönen</dt>
          <dd className="mono">
            {n(totals.bounced)} · {rate(totals.bounceRate)}
          </dd>
        </div>
        <div>
          <dt>Gitmeye hazır</dt>
          <dd className="mono">
            {n(soon)}
            {days === null ? '' : ` · ~${n(days)} iş günü`}
          </dd>
        </div>
      </dl>
      <OutreachBars daily={view.daily} />
      <p className="au-sum__meta">
        <span>
          {RESULT_TARGETS} Güncellendi {formatRelativeClock(view.asOf, clock)}
        </span>
        <button type="button" className="btn btn--secondary btn--small" onClick={onReload} disabled={loading}>
          <Icon name="refresh-cw" size={14} />
          {loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </p>
      {error === null ? null : (
        <p className="au-sum__error" role="status">
          Yenilenemedi: {error}
        </p>
      )}
    </section>
  );
}

function StateChip({ mail }: { mail: OutreachLogMail }) {
  if (mail.stage !== null) {
    const tone =
      mail.stage === 'blocked'
        ? ' pm-chip--bad'
        : mail.stage === 'ready'
          ? ' pm-chip--warn'
          : mail.stage === 'scheduled' || mail.stage === 'approved'
            ? ' pm-chip--ok'
            : '';
    return <span className={`pm-chip${tone}`}>{QUEUE_STAGE_LABELS[mail.stage].label}</span>;
  }
  if (mail.state === 'sent') return <span className="pm-chip pm-chip--ok">Gönderildi</span>;
  if (mail.state === 'failed') return <span className="pm-chip pm-chip--bad">{mail.detail ?? 'Gönderilemedi'}</span>;
  return <span className="pm-chip">{MAIL_STATE_LABELS[mail.state] || mail.state}</span>;
}

function ReplyChip({ reply }: { reply: NonNullable<OutreachLogMail['reply']> }) {
  const key = reply.classification;
  const tone =
    key !== null && INTERESTED.has(key)
      ? ' pm-chip--ok'
      : key !== null && NOT_INTERESTED.has(key)
        ? ' pm-chip--bad'
        : '';
  return (
    <span className={`pm-chip${tone}`}>
      Cevap: {key === null ? 'okunmadı' : OUTREACH_REPLY_CLASS_TR[key]} · {formatDayMonth(reply.at)}
    </span>
  );
}

/**
 * v0.1.202 (Sercan, 18 Oct 2026: "firmalar için kodlar ekle ki kimin tıkladığını görelim"): the pages the firm opened
 * on cadgaragepro.com from this mail's link. Visits only in the first two minutes after the send are most likely the
 * recipient's mail filter opening the link, and say so.
 */
function ClickChip({ clicks }: { clicks: NonNullable<OutreachLogMail['clicks']> }) {
  // Kontrol listesi B6: only scanners opened it (no browser language, HEAD, a burst of pages, or the first two minutes).
  if (clicks.maybeFilter || clicks.humanPages === 0) {
    return (
      <span
        className="pm-chip"
        title="Linki yalnız makineler açtı: mail gittikten sonraki iki dakika, tarayıcı dili yok ya da saniyeler içinde çok sayfa; çoğu zaman alıcının mail filtresi"
      >
        Link açıldı (filtre olabilir)
      </span>
    );
  }
  return (
    <span className="pm-chip pm-chip--ok">
      Linke tıklandı · {clicks.humanPages} sayfa açılışı · {formatDayMonth(clicks.firstAt)}
    </span>
  );
}

/**
 * v0.1.170 (Sercan, 11 Oct 2026: "mail gövdelerini de görebileyim, orijinal dil ve Türkçe olarak"): the opened row's
 * text, Turkish on top and the English that goes (or went) underneath, then the company's answer the same way.
 */
function MailText({ messageId }: { messageId: string }) {
  const { store, state } = useOutreachLog();
  const entry = state.texts[messageId];
  const missing = entry === undefined;
  // Read on opening, and again after Yenile cleared the texts while the row stayed open.
  useEffect(() => {
    if (missing) void store.loadText(messageId);
  }, [store, messageId, missing]);
  if (entry === undefined || entry.loading) {
    return (
      <p className="ol-text__status" role="status">
        Metin yükleniyor…
      </p>
    );
  }
  if (entry.text === null) {
    return (
      <p className="ol-text__status" role="status">
        Metin yüklenemedi: {entry.error}{' '}
        <button type="button" className="text-btn" onClick={() => void store.loadText(messageId)}>
          Tekrar dene
        </button>
      </p>
    );
  }
  const text = entry.text;
  return (
    <div className="ol-text">
      <section className="outbox-block" aria-label="Türkçesi">
        <h4 className="lr-sub">Türkçesi</h4>
        {text.subjectTr === null ? null : <p className="ot-mail__subject">{text.subjectTr}</p>}
        <p className="outbox-block__tr">{text.bodyTr ?? 'Türkçe okunuş yok.'}</p>
      </section>
      <section className="outbox-block" aria-label="İngilizce, gidecek ya da giden metin">
        <h4 className="lr-sub">İngilizce (giden metin)</h4>
        {text.subject === null ? null : (
          <p lang="en" className="ot-mail__subject">
            {text.subject}
          </p>
        )}
        <p lang="en" className="outbox-block__en outbox-block__en--reply">
          {text.body ?? 'Metin yok.'}
        </p>
      </section>
      {text.reply === null ? null : (
        <section className="outbox-block" aria-label="Firmanın cevabı">
          <h4 className="lr-sub">
            Cevap · {formatDayMonth(text.reply.at)} {formatClock(text.reply.at)}
            {text.reply.classification === null ? '' : ` · ${OUTREACH_REPLY_CLASS_TR[text.reply.classification]}`}
          </h4>
          {text.reply.textTr === null ? null : <p className="outbox-block__tr">{text.reply.textTr}</p>}
          <p lang="en" className="outbox-block__en">
            {text.reply.text ?? 'Cevabın metni saklama süresi dolduğu için silindi.'}
          </p>
        </section>
      )}
    </div>
  );
}

function MailRow({ mail }: { mail: OutreachLogMail }) {
  const [open, setOpen] = useState(false);
  const brand = CAMPAIGN_LABELS[mail.campaignCode] ?? mail.campaignCode;
  const step = mail.step <= 1 ? 'ilk mail' : `${n(mail.step - 1)}. takip`;
  return (
    <li className="ol-row">
      <div className="ol-row__firm">
        <span className="ol-row__company">{mail.company}</span>
        <span className="ol-row__address">
          {mail.recipient ?? mail.domain ?? 'adres yok'}
          {mail.region === null ? '' : ` · ${mail.region}`}
        </span>
      </div>
      <div className="ol-row__mail">
        <span className="ol-row__subject">{mail.subject ?? 'Konu henüz yazılmadı'}</span>
        <span className="ol-row__meta">
          {brand} · {step}
          {mail.stage === null ? '' : ` · ${QUEUE_STAGE_LABELS[mail.stage].text}`}
        </span>
      </div>
      <div className="ol-row__state">
        <StateChip mail={mail} />
        {mail.reply === null ? null : <ReplyChip reply={mail.reply} />}
        {mail.clicks === null ? null : <ClickChip clicks={mail.clicks} />}
        {mail.at === null ? null : <span className="ol-row__when mono">{when(mail.at)}</span>}
        <button type="button" className="text-btn ol-row__toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? 'Metni gizle' : 'Metni göster'}
        </button>
      </div>
      {open ? <MailText messageId={mail.messageId} /> : null}
    </li>
  );
}

function matches(mail: OutreachLogMail, q: string, campaign: string): boolean {
  if (campaign !== 'all' && mail.campaignCode !== campaign) return false;
  if (q === '') return true;
  const text = [mail.company, mail.domain, mail.recipient, mail.subject, mail.region].join(' ').toLocaleLowerCase('tr');
  return text.includes(q);
}

function GroupTable({
  caption,
  rows,
  name,
}: {
  caption: string;
  rows: readonly OutreachLogGroup[];
  name: (key: string) => string;
}) {
  if (rows.length === 0) return <p className="sx-empty">Henüz giden ilk mail yok.</p>;
  return (
    <table className="sx-table">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Grup</th>
          <th scope="col">Giden</th>
          <th scope="col">Cevap</th>
          <th scope="col">İlgilenen</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <th scope="row">{name(row.key)}</th>
            <td className="mono">{n(row.sent)}</td>
            <td className="mono">{n(row.replied)}</td>
            <td className="mono">{n(row.interested)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function OutreachLogPage() {
  const { store, state } = useOutreachLog();
  const desktop = useIsDesktop();
  const [query, setQuery] = useState('');
  const [campaign, setCampaign] = useState('all');
  const searchId = useId();
  useEffect(() => {
    void store.reload();
  }, [store]);
  const view = state.view;
  if (view === null) {
    return state.error === null ? (
      <div className="loading" role="status">
        Yükleniyor…
      </div>
    ) : (
      <div className="shop-status" role="status">
        Tanıtım takibi yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
          Tekrar dene
        </button>
      </div>
    );
  }
  const q = query.trim().toLocaleLowerCase('tr');
  const queue = view.queue.filter((mail) => matches(mail, q, campaign));
  const sent = view.sent.filter((mail) => matches(mail, q, campaign));
  const waitingTotal = OUTREACH_QUEUE_STAGES.reduce((sum, stage) => sum + view.totals.queue[stage], 0);
  const campaigns = ['all', ...Object.keys(CAMPAIGN_LABELS)];
  return (
    <div className={desktop ? 'au au--desk ol' : 'au ol'}>
      <Summary view={view} loading={state.loading} error={state.error} onReload={() => void store.reload()} />

      <section className="sx-section" aria-labelledby="ol-queue-h">
        <h2 id="ol-queue-h" className="sx-section__title">
          Sıradakiler <span className="mono ink-2">{n(waitingTotal)}</span>
        </h2>
        <p className="sx-sublabel">
          Taranıp adresi bulunan her firmanın ilk maili burada sıraya girer: adres kontrolü, Denetçi'nin denetimi, senin
          onayın, sonra gönderim saati. Onay{' '}
          <Link to="/tanitim-gonderimi" className="ol-link">
            Tanıtım gönderimi
          </Link>{' '}
          sayfasında.
        </p>
        <ul className="ol-stages" aria-label="Sıradaki ilk maillerin aşamaları">
          {OUTREACH_QUEUE_STAGES.map((stage) => (
            <li key={stage} className={`ol-stage ol-stage--${stage}`}>
              <span className="ol-stage__count mono">{n(view.totals.queue[stage])}</span>
              <span className="ol-stage__label">{QUEUE_STAGE_LABELS[stage].label}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="ol-filters">
        <div className="ol-field">
          <label htmlFor={searchId} className="ol-field__label">
            Ara
          </label>
          <input
            id={searchId}
            type="search"
            className="pm-input"
            placeholder="Firma, adres, konu…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <fieldset className="ol-campaigns">
          <legend className="sr-only">Kampanya</legend>
          {campaigns.map((code) => (
            <button
              key={code}
              type="button"
              className={campaign === code ? 'chip chip--on' : 'chip'}
              aria-pressed={campaign === code}
              onClick={() => setCampaign(code)}
            >
              {code === 'all' ? 'Hepsi' : CAMPAIGN_LABELS[code]}
            </button>
          ))}
        </fieldset>
      </div>

      <section className="sx-section" aria-labelledby="ol-wait-h">
        <h3 id="ol-wait-h" className="sx-section__title">
          Bekleyen ilk mailler <span className="mono ink-2">{n(queue.length)}</span>
        </h3>
        {queue.length === 0 ? (
          <p className="sx-empty">{view.queue.length === 0 ? 'Sırada mail yok.' : 'Aramaya uyan bekleyen mail yok.'}</p>
        ) : (
          <ul className="ol-rows" aria-label="Bekleyen ilk mailler">
            {queue.map((mail) => (
              <MailRow key={mail.messageId} mail={mail} />
            ))}
          </ul>
        )}
        {view.queue.length < waitingTotal ? (
          <p className="sx-sublabel">İlk {n(view.queue.length)} tanesi gösteriliyor.</p>
        ) : null}
      </section>

      <section className="sx-section" aria-labelledby="ol-sent-h">
        <h3 id="ol-sent-h" className="sx-section__title">
          Gidenler <span className="mono ink-2">{n(sent.length)}</span>
        </h3>
        {sent.length === 0 ? (
          <p className="sx-empty">
            {view.sent.length === 0
              ? 'Henüz giden mail yok. Kampanya onaylanıp gönderim anahtarı açılınca ilk mailler burada görünür.'
              : 'Aramaya uyan giden mail yok.'}
          </p>
        ) : (
          <ul className="ol-rows" aria-label="Giden mailler">
            {sent.map((mail) => (
              <MailRow key={mail.messageId} mail={mail} />
            ))}
          </ul>
        )}
        {view.sent.length < view.totals.sent ? (
          <p className="sx-sublabel">Son {n(view.sent.length)} mail gösteriliyor.</p>
        ) : null}
      </section>

      <ul className="au-list ol-groups" aria-label="Kırılımlar">
        <li>
          <article className="au-card" aria-labelledby="ol-region-h">
            <h3 id="ol-region-h" className="au-card__name">
              Bölgeye göre
            </h3>
            <GroupTable caption="İlk maili giden firmalar, bölgeye göre" rows={view.byRegion} name={(key) => key} />
          </article>
        </li>
        <li>
          <article className="au-card" aria-labelledby="ol-arm-h">
            <h3 id="ol-arm-h" className="au-card__name">
              Hangi mail önce
            </h3>
            <GroupTable
              caption="İlk maili giden firmalar, önce giden maile göre"
              rows={view.byArm}
              name={(key) => OUTREACH_ARM_TR[key] ?? key}
            />
          </article>
        </li>
      </ul>
      <p className="au-foot">
        Buradan hiçbir şey gitmez. Cevap oranı, cevap veren firma sayısının teslim edilen maillere oranıdır; otomatik
        cevaplar sayılmaz. Günler Türkiye saatine göredir.
      </p>
    </div>
  );
}
